import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Upload, Trash2, Plus, Download, Info, CheckCircle2, Circle, Snowflake, Maximize2 } from "lucide-react";
import { jsPDF } from "jspdf";
import Lightbox from "@/components/Lightbox";

const EMPTY_ARTWORK = { title: "", medium: "", size: "", price: "", image_url: "", image_filename: "" };

const LAYOUT_IMG = "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/of2oj91f_layout%20gallery.webp";

// Approximate centres of each gallery hall on the floor-plan image (percent-based, image is portrait).
const LAYOUT_LABELS = [
  { label: "I",   x: 62, y: 27 },
  { label: "II",  x: 60, y: 36 },
  { label: "III", x: 55, y: 50 },
  { label: "IV",  x: 59, y: 61 },
];

function formatSlot(s) {
  if (!s) return "";
  const opts = { weekday: "short", day: "numeric", month: "short" };
  const start = new Date(s.start_date + "T00:00:00").toLocaleDateString("en-IN", opts);
  const end = new Date(s.end_date + "T00:00:00").toLocaleDateString("en-IN", opts);
  return `${start} → ${end}`;
}

export default function GalleryApply() {
  const nav = useNavigate();
  const [options, setOptions] = useState({});
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showLayout, setShowLayout] = useState(false);

  const [form, setForm] = useState({
    artist_name: "", email: "", phone: "", whatsapp: "", website: "", instagram: "",
    artist_photo_url: "", artist_photo_filename: "",
    cv_url: "", cv_filename: "",
    exhibition_title: "",
    artist_statement: "", expected_works: 0,
    preferred_option: "gallery-1",
    preferred_slot_ids: [],
    additional_notes: "",
    artworks: [ {...EMPTY_ARTWORK}, {...EMPTY_ARTWORK}, {...EMPTY_ARTWORK}, {...EMPTY_ARTWORK}, {...EMPTY_ARTWORK} ],
  });

  useEffect(() => {
    Promise.all([
      api.get("/gallery/options"),
      api.get("/gallery/slots", { params: { months: 12 } }),
    ]).then(([o, s]) => {
      setOptions(o.data.options);
      setSlots(s.data.slots);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setArtwork = (idx, k, v) => setForm((f) => {
    const artworks = [...f.artworks];
    artworks[idx] = { ...artworks[idx], [k]: v };
    return { ...f, artworks };
  });
  const addArtwork = () => setForm((f) => ({ ...f, artworks: [...f.artworks, {...EMPTY_ARTWORK}] }));
  const removeArtwork = (idx) => setForm((f) => ({ ...f, artworks: f.artworks.filter((_, i) => i !== idx) }));

  const uploadFile = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data } = await api.post("/gallery/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const onArtistPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadFile(file, "artist-photo");
      setForm((f) => ({ ...f, artist_photo_url: res.url, artist_photo_filename: res.filename }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    }
  };
  const onCV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadFile(file, "cv");
      setForm((f) => ({ ...f, cv_url: res.url, cv_filename: res.filename }));
      toast.success("CV uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    }
  };
  const onArtworkImage = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadFile(file, "artwork");
      setForm((f) => {
        const artworks = [...f.artworks];
        artworks[idx] = { ...artworks[idx], image_url: res.url, image_filename: res.filename };
        return { ...f, artworks };
      });
      toast.success(`Artwork ${idx + 1} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    }
  };

  const toggleSlot = (id) => setForm((f) => {
    const s = new Set(f.preferred_slot_ids);
    if (s.has(id)) s.delete(id); else if (s.size < 3) s.add(id);
    return { ...f, preferred_slot_ids: [...s] };
  });

  const availableSlots = slots.filter((s) => s.availability?.[form.preferred_option] === "open");
  const optionInfo = options[form.preferred_option];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.exhibition_title.trim()) return toast.error("Please enter an exhibition title");
    if (!form.artist_photo_url) return toast.error("Please upload your photo");
    if (!form.cv_url) return toast.error("Please upload your CV / Biodata");
    if (!form.artist_statement.trim()) return toast.error("Please write your artist statement");
    if (form.preferred_slot_ids.length === 0) return toast.error("Please choose at least one preferred slot");
    const validArtworks = form.artworks.filter((a) => a.image_url);
    if (validArtworks.length < 5) return toast.error("Please upload images for all 5 artworks");

    setSubmitting(true);
    try {
      const payload = { ...form, expected_works: Number(form.expected_works) || 0, artworks: validArtworks };
      const { data } = await api.post("/gallery/applications", payload);
      toast.success("Application submitted");
      nav(`/gallery/apply/received/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const M = 40;
      let y = 50;
      const line = (t, sz = 11, bold = false) => {
        doc.setFont("helvetica", bold ? "bold" : "normal"); doc.setFontSize(sz);
        const wrapped = doc.splitTextToSize(t || "", W - M * 2);
        doc.text(wrapped, M, y);
        y += wrapped.length * (sz * 1.2);
      };
      const sep = () => { y += 8; doc.setDrawColor(180); doc.line(M, y, W - M, y); y += 14; };
      const nl = (n = 6) => { y += n; };

      line("CARPE DIEM GOA", 9, true); line("Gallery Exhibition Application", 20, true); nl();
      line("Please email this filled PDF along with your CV and artwork photos to info@carpediemgoa.com", 9); sep();

      line("ARTIST", 9, true); nl(4);
      line(`Name: ${form.artist_name || "—"}`);
      line(`Email: ${form.email || "—"}`);
      line(`Phone: ${form.phone || "—"}`);
      if (form.whatsapp) line(`WhatsApp: ${form.whatsapp}`);
      if (form.website) line(`Website: ${form.website}`);
      if (form.instagram) line(`Instagram: ${form.instagram}`);
      sep();

      const opt = options[form.preferred_option];
      line("PREFERRED SPACE", 9, true); nl(4);
      line(`${opt?.label || form.preferred_option} — ₹ ${opt?.total?.toLocaleString("en-IN") || "—"} for 5 days${opt?.ac ? " · Air-conditioned" : ""}`);
      nl();
      line("Preferred dates (in order of preference):", 10, true); nl(2);
      form.preferred_slot_ids.forEach((sid, i) => {
        const s = slots.find((x) => x.id === sid);
        line(`  ${i + 1}. ${s ? formatSlot(s) : sid}`);
      });
      sep();

      line("ARTIST STATEMENT", 9, true); nl(4);
      if (form.exhibition_title) { line(`Exhibition title: ${form.exhibition_title}`, 11, true); nl(4); }
      line(form.artist_statement || "—");
      nl();
      line(`Expected works to be displayed: ${form.expected_works || "—"}`);
      sep();

      line("ARTWORKS", 9, true); nl(4);
      form.artworks.forEach((a, i) => {
        if (!a.title && !a.medium && !a.size && !a.price) return;
        line(`${i + 1}. ${a.title || "Untitled"}`, 11, true);
        line(`   Medium: ${a.medium || "—"} · Size: ${a.size || "—"} · Price: ${a.price || "—"}`);
        if (a.image_filename) line(`   Image: ${a.image_filename}`, 9);
        nl(4);
      });
      sep();

      line("ADDITIONAL NOTES", 9, true); nl(4);
      line(form.additional_notes || "—");
      sep();

      line("TERMS", 9, true); nl(4);
      const terms = [
        "Approval of this application is at the sole discretion of Carpe Diem Goa.",
        "The gallery will confirm available dates and gallery hall(s) allocated on approval.",
        "On approval, the full space fee is payable within 2 weeks of the confirmation date.",
        "Weeks 1 and 2 of each month are the only slots open for artist exhibitions; weeks 3 and 4 are reserved for gallery-curated events.",
        "Gallery IV is air-conditioned.",
      ];
      terms.forEach((t) => { line("•  " + t, 10); nl(2); });

      doc.save(`carpe-diem-gallery-application-${(form.artist_name || "artist").replace(/\s+/g, "-").toLowerCase()}.pdf`);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="p-24 text-muted2">Loading…</div>;

  return (
    <div data-testid="gallery-apply-page" className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 lg:py-24">
      <Toaster position="top-center" richColors />

      <Link to="/gallery" className="text-sm link-hover">← Back to Gallery</Link>
      <div className="mt-6 overline">Exhibit with us</div>
      <h1 className="display text-5xl md:text-7xl mt-4">
        Apply to <em className="italic text-terra">exhibit.</em>
      </h1>
      <p className="mt-6 max-w-2xl text-muted2 text-base leading-relaxed">
        Carpe Diem Goa opens its four gallery halls to visiting and Goa-based artists. Please share your work
        and preferred dates below. Approval is at the discretion of the gallery — we&apos;ll confirm the
        available dates and hall allocation. On confirmation, the full space fee is due within 2 weeks.
      </p>

      {/* Gallery layout preview */}
      <section className="mt-14 grid lg:grid-cols-12 gap-10 items-center" data-testid="gallery-layout-section">
        <div className="lg:col-span-4 order-2 lg:order-1">
          <div className="overline">Gallery layout</div>
          <h2 className="display text-3xl md:text-4xl mt-3">Four halls, one roof.</h2>
          <p className="mt-4 text-sm text-muted2 leading-relaxed">
            Halls <strong>I</strong> and <strong>II</strong> sit adjacent on the ground level and can be booked
            singly or together as one connected space. Halls <strong>III</strong> and <strong>IV</strong> are on
            the opposite wing — Hall IV is our air-conditioned space and pairs naturally with III for larger shows.
          </p>
          <ul className="mt-6 text-xs space-y-1 text-muted2">
            <li>Gallery I — solo hall</li>
            <li>Galleries I &amp; II — connected pair</li>
            <li>Gallery IV — solo hall <span className="inline-flex items-center gap-1 text-sage" style={{color:"#4A5D4E"}}><Snowflake size={11}/> A/C</span></li>
            <li>Galleries III &amp; IV — connected pair</li>
          </ul>
          <button
            type="button"
            onClick={() => setShowLayout(true)}
            className="mt-6 inline-flex items-center gap-2 text-xs hover:text-terra transition group"
            data-testid="gallery-layout-enlarge"
          >
            <Maximize2 size={14}/><span className="border-b border-charcoal group-hover:border-terra pb-0.5">Enlarge floor plan</span>
          </button>
        </div>
        <div className="lg:col-span-8 order-1 lg:order-2">
          <button
            type="button"
            onClick={() => setShowLayout(true)}
            className="block w-full text-left border border-soft overflow-hidden bg-white group relative"
            data-testid="gallery-layout-open"
          >
            <img
              src={LAYOUT_IMG}
              alt="Carpe Diem Goa — gallery halls floor plan"
              className="w-full h-auto object-contain reveal-img"
              loading="lazy"
            />
            {/* Roman numeral badges overlaid on each hall */}
            {LAYOUT_LABELS.map(({ label, x, y }) => (
              <span
                key={label}
                className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
                style={{ left: `${x}%`, top: `${y}%` }}
              >
                <span
                  className="font-serif italic text-lg md:text-2xl leading-none flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full"
                  style={{ background: "#F9F8F6", color: "#C05C42", border: "1.5px solid #C05C42", boxShadow: "0 2px 12px rgba(0,0,0,0.15)" }}
                >
                  {label}
                </span>
                <span
                  className="hidden md:block mt-1 text-[10px] tracking-widest uppercase px-1.5 py-0.5"
                  style={{ background: "rgba(249,248,246,0.92)", color: "#5C5C55" }}
                >
                  Gallery {label}
                </span>
              </span>
            ))}
          </button>
          <p className="mt-3 text-[11px] text-muted2 leading-relaxed">Halls I–IV highlighted in blue on the floor plan. Tap the image to enlarge.</p>
        </div>
      </section>

      <form onSubmit={submit} className="mt-16 grid lg:grid-cols-12 gap-10" data-testid="gallery-apply-form">
        <div className="lg:col-span-8 space-y-14">

          {/* Contact */}
          <section>
            <div className="overline">01 · Artist</div>
            <h2 className="display text-3xl md:text-4xl mt-3">About you.</h2>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <input required placeholder="Full name" value={form.artist_name} onChange={(e)=>setField("artist_name",e.target.value)} className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal" data-testid="apply-name" />
              <input required type="email" placeholder="Email (required)" value={form.email} onChange={(e)=>setField("email",e.target.value)} className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal" data-testid="apply-email" />
              <input required placeholder="Phone" value={form.phone} onChange={(e)=>setField("phone",e.target.value)} className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal" data-testid="apply-phone" />
              <input placeholder="WhatsApp (if different)" value={form.whatsapp} onChange={(e)=>setField("whatsapp",e.target.value)} className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal" />
              <input placeholder="Website" value={form.website} onChange={(e)=>setField("website",e.target.value)} className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal" />
              <input placeholder="Instagram handle" value={form.instagram} onChange={(e)=>setField("instagram",e.target.value)} className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal" />
            </div>

            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <UploadTile
                label="Artist photograph (required)"
                accept="image/*"
                filename={form.artist_photo_filename}
                previewUrl={form.artist_photo_url}
                onChange={onArtistPhoto}
                testId="upload-artist-photo"
              />
              <UploadTile
                label="CV / Biodata (required · PDF / DOC)"
                accept=".pdf,.doc,.docx"
                filename={form.cv_filename}
                previewUrl={null}
                onChange={onCV}
                testId="upload-cv"
                isDocument
              />
            </div>
          </section>

          {/* Statement */}
          <section>
            <div className="overline">02 · Practice</div>
            <h2 className="display text-3xl md:text-4xl mt-3">Your statement.</h2>
            <p className="text-sm text-muted2 mt-2">A working title for your show, followed by a paragraph or two on why you do what you do.</p>
            <input
              required
              placeholder="Exhibition title (required)"
              value={form.exhibition_title}
              onChange={(e)=>setField("exhibition_title",e.target.value)}
              className="mt-6 w-full bg-transparent border-b border-soft py-3 text-base font-serif focus:outline-none focus:border-charcoal"
              data-testid="apply-exhibition-title"
            />
            <textarea required rows={7} value={form.artist_statement} onChange={(e)=>setField("artist_statement",e.target.value)} className="mt-6 w-full bg-transparent border border-soft p-4 text-sm focus:outline-none focus:border-charcoal" placeholder="I make paintings that…" data-testid="apply-statement" />
            <div className="mt-6 flex items-center gap-4">
              <label className="text-sm">Expected works to display</label>
              <input type="number" min={1} value={form.expected_works} onChange={(e)=>setField("expected_works",e.target.value)} className="w-24 bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" data-testid="apply-expected-works" />
            </div>
          </section>

          {/* Artworks */}
          <section>
            <div className="overline">03 · Works</div>
            <h2 className="display text-3xl md:text-4xl mt-3">Five artworks.</h2>
            <p className="text-sm text-muted2 mt-2">All five artwork images are required. Representative of the work you plan to display. Add title, medium, size, price for each.</p>
            <div className="mt-8 space-y-8">
              {form.artworks.map((a, i) => (
                <div key={i} className="border border-soft p-5 grid md:grid-cols-12 gap-4 items-start" data-testid={`artwork-${i}`}>
                  <div className="md:col-span-3">
                    <UploadTile
                      label={`Artwork ${i + 1}`}
                      accept="image/*"
                      filename={a.image_filename}
                      previewUrl={a.image_url}
                      onChange={(e)=>onArtworkImage(i, e)}
                      testId={`upload-artwork-${i}`}
                    />
                  </div>
                  <div className="md:col-span-9 grid grid-cols-2 gap-3">
                    <input placeholder="Title" value={a.title} onChange={(e)=>setArtwork(i,"title",e.target.value)} className="col-span-2 bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" />
                    <input placeholder="Medium (e.g. Oil on canvas)" value={a.medium} onChange={(e)=>setArtwork(i,"medium",e.target.value)} className="bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" />
                    <input placeholder="Size (e.g. 36 × 48 in)" value={a.size} onChange={(e)=>setArtwork(i,"size",e.target.value)} className="bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" />
                    <input placeholder="Price (₹ or 'On request')" value={a.price} onChange={(e)=>setArtwork(i,"price",e.target.value)} className="col-span-2 bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" />
                    {form.artworks.length > 5 && (
                      <button type="button" onClick={()=>removeArtwork(i)} className="col-span-2 justify-self-end text-xs text-muted2 hover:text-red-700 inline-flex items-center gap-1"><Trash2 size={12}/>Remove</button>
                    )}
                  </div>
                </div>
              ))}
              <button type="button" onClick={addArtwork} className="text-xs inline-flex items-center gap-1 link-hover" data-testid="artwork-add"><Plus size={14}/> Add another artwork</button>
            </div>
          </section>

          <section>
            <div className="overline">04 · Notes</div>
            <textarea rows={4} value={form.additional_notes} onChange={(e)=>setField("additional_notes",e.target.value)} placeholder="Anything else you'd like us to know (accessibility, hanging, opening event…)" className="mt-6 w-full bg-transparent border border-soft p-4 text-sm focus:outline-none focus:border-charcoal" />
          </section>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-[92px] space-y-8">
            <div className="border border-soft p-6 bg-white">
              <div className="overline">Step 1 of 2</div>
              <h3 className="font-serif text-xl mt-2 leading-snug">Select preferred gallery section</h3>
              <p className="text-xs text-muted2 mt-2">Tap to choose. You can change this any time before submitting.</p>
              <div className="mt-4 space-y-2">
                {Object.entries(options).map(([key, o]) => {
                  const selected = form.preferred_option === key;
                  return (
                    <button
                      key={key} type="button"
                      onClick={() => { setField("preferred_option", key); setField("preferred_slot_ids", []); }}
                      className={`w-full text-left border-2 p-3 transition relative ${selected ? "border-terra bg-cream-2" : "border-soft hover:border-charcoal"}`}
                      style={selected ? { boxShadow: "0 2px 12px rgba(192,92,66,0.18)" } : {}}
                      data-testid={`space-option-${key}`}
                      aria-pressed={selected}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="font-serif text-lg flex items-center gap-2">
                          {selected && <CheckCircle2 size={16} className="text-terra" style={{color:"#C05C42"}}/>}
                          <span>{o.label}</span>
                          {o.ac && <Snowflake size={12} className="inline text-sage ml-1"/>}
                        </div>
                        <div className="font-serif">₹ {o.total.toLocaleString("en-IN")}</div>
                      </div>
                      <div className="text-xs text-muted2 mt-1 pl-6">₹ {o.per_day.toLocaleString("en-IN")} × 5 days · Wed → Sun</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border border-soft p-6 bg-white">
              <div className="overline">Step 2 of 2</div>
              <h3 className="font-serif text-xl mt-2 leading-snug">Preferred dates</h3>
              <p className="text-xs text-muted2 mt-2">Choose up to 3 preferences from the calendar of available Wed → Sun slots for {optionInfo?.label}. Only weeks 1 &amp; 2 of each month are exhibitable. Slots less than 3 weeks away are not shown so we have time to organise.</p>
              <div className="mt-4 max-h-[420px] overflow-y-auto pr-1 space-y-1">
                {availableSlots.length === 0 && <div className="text-xs text-muted2 py-4">No open slots for this space in the next 12 months.</div>}
                {availableSlots.map((s) => {
                  const picked = form.preferred_slot_ids.includes(s.id);
                  const rank = picked ? form.preferred_slot_ids.indexOf(s.id) + 1 : null;
                  return (
                    <button
                      key={s.id} type="button" onClick={() => toggleSlot(s.id)}
                      className={`w-full text-left border p-3 flex items-center justify-between transition ${picked ? "border-terra bg-cream-2" : "border-soft hover:border-charcoal"}`}
                      data-testid={`slot-${s.id}`}
                    >
                      <div>
                        <div className="text-sm">{formatSlot(s)}</div>
                        <div className="text-[10px] text-muted2 tracking-widest uppercase">{s.month_label} · Week {s.week}</div>
                      </div>
                      {picked ? <span className="w-5 h-5 rounded-full bg-terra text-white text-xs flex items-center justify-center font-medium">{rank}</span> : <Circle size={16} className="text-muted2"/>}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 text-[11px] text-muted2 leading-relaxed">
                Selected {form.preferred_slot_ids.length}/3
              </div>
            </div>

            <div className="border border-soft p-6 bg-cream-2 text-xs leading-relaxed text-muted2">
              <div className="flex items-start gap-2"><Info size={13} className="text-sage mt-0.5"/><div>
                Approval is at the discretion of the gallery. We&apos;ll confirm the exact dates and hall(s). On confirmation the full space fee is due within 2 weeks.
              </div></div>
            </div>

            <div className="flex flex-col gap-3">
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="apply-submit">
                {submitting ? "Submitting…" : "Submit application"}
              </button>
              <button type="button" onClick={downloadPDF} disabled={downloading} className="btn-ghost w-full justify-center" data-testid="apply-download-pdf">
                <Download size={14}/> {downloading ? "Preparing…" : "Download filled form (PDF)"}
              </button>
              <p className="text-[11px] text-muted2 text-center">Submissions are emailed to info@carpediemgoa.com and stored for review.</p>
            </div>
          </div>
        </aside>
      </form>

      {showLayout && (
        <Lightbox
          images={[{ url: LAYOUT_IMG, caption: "Gallery layout · Carpe Diem Goa" }]}
          index={0}
          onIndex={() => {}}
          onClose={() => setShowLayout(false)}
        />
      )}
    </div>
  );
}

function UploadTile({ label, accept, filename, previewUrl, onChange, testId, isDocument = false }) {
  return (
    <label className="block cursor-pointer group" data-testid={testId}>
      <div className="border border-dashed border-soft aspect-square flex items-center justify-center overflow-hidden bg-cream-2 hover:border-charcoal transition">
        {previewUrl && !isDocument ? (
          <img src={previewUrl} alt={label} className="w-full h-full object-cover"/>
        ) : filename ? (
          <div className="p-4 text-center">
            <CheckCircle2 className="mx-auto text-sage" size={22} style={{color:"#4A5D4E"}}/>
            <div className="text-xs mt-2 break-all">{filename}</div>
          </div>
        ) : (
          <div className="p-4 text-center">
            <Upload className="mx-auto text-muted2" size={22}/>
            <div className="text-xs text-muted2 mt-2">Click to upload</div>
          </div>
        )}
      </div>
      <div className="mt-2 overline">{label}</div>
      <input type="file" accept={accept} onChange={onChange} className="hidden"/>
    </label>
  );
}
